/**
 * Post a status.
 *
 * The two buttons on the Updates tab led nowhere — reported as "I can't
 * upload status", which was exactly right.
 *
 * Built to WhatsApp's released text-status composer, not invented:
 *
 *   The whole screen IS the status. A full-bleed coloured canvas with the
 *   text centred on it, so what you compose is what gets posted. My first
 *   attempt made it a small card inside a form, which showed you something
 *   that was not what anyone would see.
 *
 *   Text is always white. WhatsApp does not let you change it, which is
 *   what lets every background be chosen freely without a contrast check.
 *
 *   The palette and the T CYCLE. Tapping the palette moves to the next
 *   background, tapping the T to the next font. That is the released
 *   behaviour; the bottom selection bar is an Android beta and is
 *   deliberately not copied.
 *
 *   A round send button, bottom right. Not a form submit.
 *
 * Both kinds expire after 24 hours; the service sets that, not this screen.
 */

import { useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  status as statusService, media, userMessage, isWithinLimit, MAX_UPLOAD_BYTES, formatSize
} from '@zivapp/shared'
import './NewStatus.css'

/**
 * Backgrounds, cycled by the palette.
 *
 * Deep and saturated only: the text on top is always white, so a pale
 * background would leave it unreadable. Kept away from the brand green so
 * a status never reads as app chrome.
 */
const BACKGROUNDS = [
  '#0f766e', '#7c2d12', '#1e3a8a', '#581c87', '#9f1239',
  '#3f3f46', '#14532d', '#78350f', '#155e75', '#86198f'
]

/**
 * Fonts, cycled by the T.
 *
 * Stacks rather than webfonts: a status composer must not wait on a font
 * download to render the thing you are typing.
 */
const FONTS = [
  { name: 'Sans',       css: 'var(--ziv-font)', weight: 500 },
  { name: 'Serif',      css: 'Georgia, "Times New Roman", serif', weight: 500 },
  { name: 'Typewriter', css: 'var(--ziv-font-mono)', weight: 500 },
  { name: 'Bold',       css: 'var(--ziv-font)', weight: 800 },
  { name: 'Light',      css: 'var(--ziv-font)', weight: 300 }
]

/** Quick inserts, not an emoji keyboard. */
const EMOJI = ['😀','😂','🥹','😍','🤔','🙌','🔥','🎉','❤️','👍','🙏','💯']

export default function NewStatus () {
  const { kind } = useParams()          // 'text' | 'photo'
  const navigate = useNavigate()
  const fileRef = useRef(null)

  const isPhoto = kind === 'photo'

  const [body, setBody] = useState('')
  const [bgIndex, setBgIndex] = useState(0)
  const [fontIndex, setFontIndex] = useState(0)
  const [emojiOpen, setEmojiOpen] = useState(false)
  const [file, setFile] = useState(null)
  const [preview, setPreview] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  const background = BACKGROUNDS[bgIndex]
  const font = FONTS[fontIndex]

  const nextBackground = () => setBgIndex(i => (i + 1) % BACKGROUNDS.length)
  const nextFont = () => setFontIndex(i => (i + 1) % FONTS.length)

  function pickFile (event) {
    const chosen = event.target.files?.[0]
    if (!chosen) return

    // MAX_UPLOAD_BYTES is keyed by kind, so comparing a number to the
    // object was always false and this check never fired.
    if (!isWithinLimit(chosen)) {
      setError(
        `That photo is ${formatSize(chosen.size)}. ` +
        `The limit is ${formatSize(MAX_UPLOAD_BYTES.image)}.`
      )
      return
    }

    setError(null)
    setFile(chosen)
    // A local preview, so the picture appears instantly rather than after
    // a round trip it has not even started.
    setPreview(URL.createObjectURL(chosen))
  }

  async function send () {
    if (busy) return

    const text = body.trim()
    if (!isPhoto && !text) return
    if (isPhoto && !file) return

    setBusy(true)
    setError(null)
    try {
      let mediaUrl = null
      if (isPhoto) {
        const uploaded = await media.upload(file)
        mediaUrl = uploaded.url
      }

      await statusService.post(
        isPhoto
          ? { type: 'image', mediaUrl, body: text || null }
          : { type: 'text', body: text, backgroundColor: background, font: font.name }
      )
      navigate('/updates')
    } catch (cause) {
      setError(userMessage(cause))
      setBusy(false)
    }
  }

  const canSend = isPhoto ? Boolean(file) : Boolean(body.trim())

  return (
    <div
      className={`nst${isPhoto ? ' nst--photo' : ''}`}
      style={isPhoto ? undefined : { background }}
    >
      <header className="nst__bar">
        <button
          type="button"
          className="nst__icon"
          onClick={() => navigate('/updates')}
          aria-label="Cancel"
        >
          <svg viewBox="0 0 24 24" fill="none">
            <path d="M6 6l12 12M18 6 6 18" stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" />
          </svg>
        </button>

        <span className="nst__spacer" />

        {!isPhoto && (
          <>
            {/* A T that cycles the font, as in the released app. */}
            <button
              type="button"
              className="nst__icon nst__icon--text"
              onClick={nextFont}
              aria-label={`Font: ${font.name}. Click for the next one.`}
              data-testid="status-font"
            >
              T
            </button>

            {/* A smiley that inserts emoji, as the composer has. A small
                fixed set rather than a full picker: this is a row of
                quick inserts, not an emoji keyboard. */}
            <button
              type="button"
              className="nst__icon"
              onClick={() => setEmojiOpen(open => !open)}
              aria-label="Insert emoji"
              aria-expanded={emojiOpen}
              data-testid="status-emoji"
            >
              <svg viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="8.6" stroke="currentColor" strokeWidth="1.8" />
                <circle cx="9.3" cy="10" r="1.2" fill="currentColor" />
                <circle cx="14.7" cy="10" r="1.2" fill="currentColor" />
                <path d="M8.6 14.2a4.2 4.2 0 0 0 6.8 0" stroke="currentColor"
                  strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            </button>

            {/* A palette that cycles the background, likewise. */}
            <button
              type="button"
              className="nst__icon"
              onClick={nextBackground}
              aria-label="Next background colour"
              data-testid="status-colour"
            >
              <svg viewBox="0 0 24 24" fill="none">
                <path d="M12 3a9 9 0 0 0 0 18c.9 0 1.6-.7 1.6-1.6 0-.4-.2-.8-.4-1.1a1.6 1.6 0 0 1 1.2-2.6h1.9A4.7 4.7 0 0 0 21 11C21 6.6 17 3 12 3Z"
                  stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                <circle cx="7.5" cy="11.5" r="1.2" fill="currentColor" />
                <circle cx="11" cy="7.8" r="1.2" fill="currentColor" />
                <circle cx="15.5" cy="8.6" r="1.2" fill="currentColor" />
              </svg>
            </button>
          </>
        )}
      </header>

      {isPhoto ? (
        <>
          <button
            type="button"
            className="nst__photo"
            onClick={() => fileRef.current?.click()}
            data-testid="pick-status-photo"
          >
            {preview
              ? <img className="nst__preview" src={preview} alt="" />
              : (
                <span className="nst__photo-prompt">
                  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M4 8h2.8l1.4-2.2h7.6L17.2 8H20a1.8 1.8 0 0 1 1.8 1.8v8.4A1.8 1.8 0 0 1 20 20H4a1.8 1.8 0 0 1-1.8-1.8V9.8A1.8 1.8 0 0 1 4 8Z"
                      stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                    <circle cx="12" cy="13.8" r="3.4" stroke="currentColor" strokeWidth="1.8" />
                  </svg>
                  Choose a photo
                </span>
              )}
          </button>

          <input
            ref={fileRef}
            className="ziv-sr-only"
            type="file"
            accept="image/*"
            onChange={pickFile}
            data-testid="status-file"
          />
        </>
      ) : (
        <div className="nst__canvas">
          {emojiOpen && (
            <div className="nst__emoji" role="group" aria-label="Insert an emoji">
              {EMOJI.map(e => (
                <button
                  key={e}
                  type="button"
                  className="nst__emoji-btn"
                  onClick={() => { setBody(b => b + e); setEmojiOpen(false) }}
                >
                  {e}
                </button>
              ))}
            </div>
          )}

          <textarea
            className="nst__text"
            style={{ fontFamily: font.css, fontWeight: font.weight }}
            value={body}
            onChange={e => setBody(e.target.value)}
            placeholder="Type a status"
            maxLength={700}
            aria-label="Status text"
            data-testid="status-text"
            autoFocus
          />
        </div>
      )}

      {error && <p className="nst__error" role="alert">{error}</p>}

      <footer className="nst__foot">
        {isPhoto && (
          <input
            className="nst__caption"
            value={body}
            onChange={e => setBody(e.target.value)}
            placeholder="Add a caption…"
            maxLength={700}
            aria-label="Caption"
          />
        )}

        <span className="nst__expiry">Disappears after 24 hours</span>

        <button
          type="button"
          className="nst__send"
          onClick={send}
          disabled={!canSend || busy}
          aria-label="Post status"
          data-testid="post-status"
        >
          <svg viewBox="0 0 24 24" fill="none">
            <path d="M3.4 11.2 20 4l-7.2 16.6-2-6.6-7.4-2.8Z" stroke="currentColor"
              strokeWidth="1.8" strokeLinejoin="round" />
          </svg>
        </button>
      </footer>
    </div>
  )
}
